import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  apiService,
  BillingPlan,
  BillingState,
  BillingSummary,
  TrialForm,
} from '../services/apiService';
import PricingModal from './PricingModal';
import TrialFormModal from './TrialFormModal';
import CancelTrialModal, { CancelKind } from './CancelTrialModal';
import { money } from '../utils/money';
import { refreshEntitlements } from './useUpgradeGate';
import { trackClick } from '../utils/analytics';
import { checkoutTracker, waitForMembership } from '../utils/razorpayCheckout';

/**
 * Profile → Billing. Layout mirrors the billing mock: current-plan card,
 * upgrade banner, renewal row, payment method, invoice history, contact line.
 *
 * Payment flows (both via Razorpay Standard Checkout):
 *  - Subscribe (auto-renew): backend creates a Razorpay Subscription.
 *  - Pay once (no commitment): backend creates an Order. Hidden unless an
 *    admin enabled it for this account; the only flow coupons apply to.
 * Success hands the ids+signature to /billing/verify — the backend HMAC
 * check is what actually grants access, never the client.
 */

declare global {
  interface Window { Razorpay?: any }
}

let razorpayLoader: Promise<boolean> | null = null;
const loadRazorpay = (): Promise<boolean> => {
  if (window.Razorpay) return Promise.resolve(true);
  if (!razorpayLoader) {
    razorpayLoader = new Promise(resolve => {
      const el = document.createElement('script');
      el.src = 'https://checkout.razorpay.com/v1/checkout.js';
      el.onload = () => resolve(true);
      el.onerror = () => { razorpayLoader = null; resolve(false); };
      document.head.appendChild(el);
    });
  }
  return razorpayLoader;
};

// Amounts arrive as minor units (paise / cents / rappen) of whichever currency
// the server decided for this user — never both at once.
// Kept for the mock's "reminder" row, which is no longer rendered: we do not
// run any pre-renewal reminder job, so printing a date derived from this was
// promising the customer a message that never arrives. Restore only alongside
// a real scheduled reminder.
const remindOn = (iso: string | null | undefined) => {
  if (!iso) return null;
  const d = new Date(iso);
  d.setMonth(d.getMonth() - 1);
  return d.toISOString();
};
const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
/** Whole days until `iso`, never negative — the "(n days left)" on a running
 *  trial when the summary carries no lifecycle block (older backend). */
const daysUntil = (iso: string | null | undefined) =>
  iso ? Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)) : 0;

// The server's list is authoritative (the admin table groups by it); this is
// only so a summary from an older backend still lets someone cancel.
const TRIAL_CANCEL_REASONS = [
  'Too expensive', 'Not enough time', 'Missing features', 'Irrelevant results',
  'Difficult to use', 'Not enough technologies', "Couldn't find what I needed",
  'Technical issues', 'Just exploring', 'Other',
];

// PostHog names per exit. Kept distinct rather than one event with a kind
// prop: the trial funnel already has dashboards on the old names.
const CLICK_NAMES: Record<CancelKind, { open: string; keep: string; confirm: string }> = {
  trial: { open: 'billing_click_cancel_trial', keep: 'billing_keep_trial',
           confirm: 'billing_confirm_cancel_trial' },
  subscription: { open: 'billing_click_cancel_membership', keep: 'billing_keep_membership',
                  confirm: 'billing_confirm_cancel_membership' },
};

const BillingTab: React.FC = () => {
  const userId = apiService.getStoredUserId() || '';
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyPlan, setBusyPlan] = useState('');
  const [notice, setNotice] = useState('');
  // The come-back coupon a cancel answered with ({code, message} from the
  // server's win-back setting), shown under the success notice. Server-
  // authored: the admin can switch it off or change the code, and the tab
  // must never promise a discount the pricing function will not give.
  const [winback, setWinback] = useState<{ code: string; message: string } | null>(null);
  const [winbackCopied, setWinbackCopied] = useState(false);
  const [coupon, setCoupon] = useState('');
  // The server's answer for this coupon. Held so the price shown is the price
  // the same pricing function will charge, rather than a number computed here.
  const [quote, setQuote] = useState<any>(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponError, setCouponError] = useState('');
  // Which payment method the discount will be used with. A Razorpay
  // subscription offer covers one method, so when a code has offers for
  // several we must know which before creating the subscription.
  const [payMethod, setPayMethod] = useState('');
  const [showPricing, setShowPricing] = useState(false);
  // The trial details sheet: null when closed, else what it was opened with.
  // `plan` is the plan the customer pressed the trial on (the paywall passes
  // it; the tab's own button does not). The trial is card-free, so it is
  // informational only.
  const [trialForm, setTrialForm] = useState<{
    plan?: BillingPlan; initial: Partial<TrialForm>;
  } | null>(null);
  // The exit dialog owns its own busy/error so a failed cancel stays in the
  // modal with the reasons still ticked, instead of surfacing in the tab
  // behind it. One sheet serves both exits — a trial and a paid membership's
  // renewal — so the state holds WHICH is open rather than a yes/no.
  const [cancelSheet, setCancelSheet] = useState<CancelKind | null>(null);
  const [cancelSheetBusy, setCancelSheetBusy] = useState(false);
  const [cancelSheetError, setCancelSheetError] = useState('');
  // Which open sheet still owes a 'kept' (null: none). A ref, not state: the
  // modal can hand us two closes for one gesture (Escape with a backdrop
  // click already queued) before React re-renders, and a state flag would
  // read stale for both. Cleared by whichever exit happens first, so one open
  // logs exactly one 'opened' and at most one 'kept' — never a 'kept' on top
  // of a successful cancel. Carries the kind so the 'kept' lands in the same
  // funnel as its 'opened'.
  const cancelSheetOpen = useRef<CancelKind | null>(null);

  const refresh = async () => {
    if (!userId) { setError('Sign in to manage billing.'); setLoading(false); return; }
    setLoading(true);
    const res = await apiService.getBillingSummary(userId);
    if (res.success && res.data?.success) {
      setSummary(res.data);
      setError('');
    } else {
      setError(res.error || 'Could not load billing details');
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  /** Every action that starts something new clears the last outcome — the
   *  come-back coupon included, since a customer mid-purchase has come
   *  back. */
  const resetNotices = () => { setError(''); setNotice(''); setWinback(null); };

  /** Put the come-back code on the clipboard. The chip's text flips to
   *  "TRIAL20 copied" for a moment so the click visibly did something; a
   *  browser that refuses clipboard access (http, an old WebView) leaves the
   *  code selectable in the chip, which is why it is rendered as text and not
   *  only behind the button. */
  const copyWinback = async () => {
    if (!winback) return;
    trackClick('billing_winback_copy', { code: winback.code });
    try {
      await navigator.clipboard.writeText(winback.code);
      setWinbackCopied(true);
      setTimeout(() => setWinbackCopied(false), 2500);
    } catch { /* nothing to do: the code is on screen to select by hand */ }
  };

  const currentPlan: BillingPlan | null = useMemo(() => {
    if (!summary) return null;
    const code = summary.subscription?.status === 'active' || summary.subscription?.status === 'cancelled'
      ? summary.subscription.plan_code : 'explorer';
    return summary.plans.find(p => p.code === code) || null;
  }, [summary]);

  // Kept in step with PricingModal's MVP_PLANS: the plans it will render.
  const MODAL_PLANS = ['explorer', 'voyager'];
  const upgradeTarget: BillingPlan | null = useMemo(() => {
    if (!summary) return null;
    // A trial row names the plan on trial (Voyager), which is the plan the
    // upgrade must offer — so a trial counts as the free tier here, the
    // same way PricingModal is handed currentPlanCode 'explorer' for it.
    // Otherwise nothing above Voyager exists, the target is null and the
    // trial-active line has no Upgrade button (contract item 5).
    const subRow = summary.subscription;
    const onTrialRow = !!subRow && (subRow.is_trial || subRow.source === 'trial');
    const currentSort = onTrialRow ? -1 : (currentPlan?.sort_order ?? -1);
    return summary.plans
      // `amount` is what THIS customer pays; price_inr_paise is only the
      // rupee fallback, so a plan priced in a region but not in INR would
      // otherwise be filtered out of the upgrade list entirely.
      .filter(p => p.sort_order > currentSort && (p.amount ?? p.price_inr_paise) > 0
        && p.is_active !== false)
      .sort((a, b) => a.sort_order - b.sort_order)[0] || null;
  }, [summary, currentPlan]);

  const openCheckout = async (opts: {
    keyId: string; description: string;
    orderId?: string; subscriptionId?: string; amountPaise?: number;
    plan: string; flow: 'order' | 'subscription';
    email?: string; name?: string; currency?: string;
    payMethod?: string;
    /** For the checkout log only: the trial flag and the price the sheet is
     *  expected to charge (a subscription's first cycle, as shown). */
    trial?: boolean; expectedAmount?: number;
    /** Turning on auto-renewal for a paid period: what to say once the card
     *  is on file (nothing is paid now, so there is no membership to wait for). */
    renewalNotice?: string;
  }) => {
    const ok = await loadRazorpay();
    if (!ok || !window.Razorpay) {
      setError('Could not load the payment window — check your connection and try again.');
      return;
    }
    const trace = checkoutTracker({
      flow: opts.flow, plan: opts.plan,
      amount: opts.amountPaise ?? opts.expectedAmount ?? null,
      currency: opts.currency || null,
      method: opts.payMethod || null,
      subscriptionId: opts.subscriptionId, orderId: opts.orderId,
      trial: !!opts.trial,
      surface: 'billing_tab',
    });
    const rz = new window.Razorpay({
      key: opts.keyId,
      name: 'UniversityTech.io',
      description: opts.description,
      // Identifies the payer on the Razorpay side (dashboard + receipts).
      prefill: { email: opts.email || '', name: opts.name || '' },
      notes: { utech_user_id: userId },
      ...(opts.subscriptionId ? { subscription_id: opts.subscriptionId } : {}),
      ...(opts.orderId ? { order_id: opts.orderId, amount: opts.amountPaise,
                           currency: opts.currency || 'INR' } : {}),
      // Only set when a method-scoped discount is in play. Left alone,
      // Razorpay shows every method it supports, which is what we want for a
      // normal purchase.
      ...(opts.payMethod ? {
        method: {
          card: opts.payMethod === 'card',
          upi: opts.payMethod === 'upi',
          netbanking: false, wallet: false, emi: false, paylater: false,
        },
      } : {}),
      theme: { color: '#8f7b46' },
      handler: async (resp: any) => {
        const v = await apiService.billingVerify({
          user_id: userId,
          flow: opts.flow,
          plan_code: opts.plan,
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_signature: resp.razorpay_signature,
          razorpay_order_id: resp.razorpay_order_id,
          razorpay_subscription_id: resp.razorpay_subscription_id,
        });
        const confirmed = () => {
          setNotice('Payment confirmed — your membership is active.');
          setWinback(null);
          setCoupon('');
          // Unlock search/filters/introductions now, not on the next reload.
          refreshEntitlements();
        };
        if (opts.renewalNotice) {
          if (v.success && v.data?.success) { trace.verified(resp.razorpay_payment_id); setNotice(opts.renewalNotice); }
          else {
            trace.verifyFailed(v.error || v.data?.detail || 'verify refused', resp.razorpay_payment_id);
            setError(v.error || v.data?.detail || 'Auto-renewal could not be confirmed — reload Billing in a minute.');
          }
          setBusyPlan('');
        } else if (v.success && v.data?.success) {
          trace.verified(resp.razorpay_payment_id);
          confirmed();
        } else {
          trace.verifyFailed(v.error || v.data?.detail || 'verify refused', resp.razorpay_payment_id);
          // Razorpay has the money; the webhook may well have granted the plan.
          setNotice('Payment received — activating your membership…');
          if (await waitForMembership(userId, !!opts.trial)) {
            trace.verifiedLate(resp.razorpay_payment_id);
            confirmed();
          } else {
            setNotice('');
            setError(v.error || v.data?.detail ||
              'Payment could not be verified. If you were charged, contact info@universitytech.io.');
          }
        }
        refresh();
      },
      modal: { ondismiss: () => { trace.dismissed(); setBusyPlan(''); } },
    });
    rz.on('payment.failed', (r: any) => {
      trace.paymentFailed(r);
      setError(r?.error?.description || 'Payment failed — you have not been charged.');
      setBusyPlan('');
    });
    rz.open();
    trace.opened();
  };

  /**
   * "Start free trial": open the details sheet (Sep 2026 spec). The trial
   * asks for no card; it asks who the person is — name, organization,
   * designation — and the server will not grant it without them, so the
   * sheet stands between the button and the API on every route.
   *
   * Prefilled from the profile: someone who has already told us their
   * organization is not asked to type it twice, and the profile read is
   * best-effort — an empty sheet is a fine fallback. The tab has no profile
   * in hand (ProfilePage does not pass it down), so it is read here.
   */
  const startTrial = async (plan?: BillingPlan) => {
    resetNotices();
    if (summary?.trial && !summary.trial.available) {
      setError(summary.trial.reason || 'A trial is not available for this account right now.');
      return;
    }
    let initial: Partial<TrialForm> = {};
    const prof = await apiService.getProfile(userId);
    if (prof.success && prof.data) {
      initial = {
        first_name: prof.data.user?.first_name || '',
        last_name: prof.data.user?.last_name || '',
        organization: prof.data.profile?.organisation_name || '',
        designation: prof.data.profile?.designation || '',
      };
    }
    setTrialForm({ plan, initial });
  };

  /**
   * The sheet's Start trial: the four details go with the request, the
   * server writes them to the profile and grants access at once. No card
   * and no Razorpay sheet — the autopay trial was retired on 2026-09-23.
   */
  const submitTrialForm = async (form: TrialForm): Promise<{ ok: boolean; error?: string }> => {
    const r = await apiService.billingStartTrial(userId, form);
    if (!(r.success && r.data?.success)) {
      return { ok: false, error: r.error || r.data?.detail || 'Could not start the trial' };
    }
    setTrialForm(null);
    const days = r.data.days ?? summary?.trial?.days;
    setNotice(days ? `Your ${days}-day trial is active — enjoy.` : 'Your trial is active — enjoy.');
    // Unlock search/filters/introductions now, not on the next reload.
    refreshEntitlements();
    refresh();
    return { ok: true };
  };

  const startSubscribe = async (plan: BillingPlan, code?: string, method?: string,
                                trial = false, expected?: number) => {
    resetNotices(); setBusyPlan(plan.code);
    const res = await apiService.billingSubscribe(userId, plan.code, code, method, trial, expected);
    if (!res.success || !res.data?.success) {
      setError(res.error || 'Could not start the membership');
      setBusyPlan('');
      return;
    }
    openCheckout({
      keyId: res.data.key_id,
      description: `${plan.name} — yearly auto-renew`,
      subscriptionId: res.data.razorpay_subscription_id,
      plan: plan.code, flow: 'subscription',
      // Pin checkout to the method the offer covers. Without this the
      // customer could pick another one and be charged full price after
      // being shown the discount.
      payMethod: method,
      currency: res.data.currency,
      trial,
      expectedAmount: expected ?? res.data.plan?.amount_paise,
    });
  };

  /** "Turn on auto-renewal": a paid period (one-time, or recorded by the
   *  team) gets a card for the next one. The card is checked now and first
   *  charged on the day access ends, at the plan's listed price. */
  const startRenewal = async () => {
    const rn = summary?.renewal;
    if (!rn) return;
    resetNotices(); setBusyPlan('renewal');
    const res = await apiService.billingSubscribe(userId, rn.plan_code, undefined, undefined, false, rn.amount, true);
    if (!res.success || !res.data?.success) {
      setError(res.error || 'Could not set up auto-renewal');
      setBusyPlan('');
      return;
    }
    openCheckout({
      keyId: res.data.key_id,
      description: `${rn.plan_name} — auto-renewal from ${fmtDate(rn.charge_on)}`,
      subscriptionId: res.data.razorpay_subscription_id,
      plan: rn.plan_code, flow: 'subscription',
      currency: res.data.currency,
      expectedAmount: rn.amount,
      renewalNotice: `Auto-renewal is on. Your card will be charged ${money(rn.amount, rn.currency)} on ${fmtDate(rn.charge_on)}; you can cancel any time before then.`,
    });
  };

  /** The code to send with a checkout: what is in the box, unless the quote
   *  already refused it. The server now stops a checkout whose code does not
   *  resolve rather than charging the listed price behind it, so a code that
   *  was shown as rejected (or beaten by a campaign) must not ride along — the
   *  customer accepted the price on the button, not that code. A code typed
   *  and never applied is still sent, so a valid one is honoured. */
  const codeToSend = () => (couponError ? undefined : (coupon.trim() || undefined));
  /** The first-cycle price on the button right now — the applied quote's, or
   *  the plan's price net of any running campaign. Sent with the checkout so
   *  the server can refuse to charge more than this. */
  const shownPrice = (p: BillingPlan) =>
    (quote && !couponError && quote.plan?.code === p.code ? quote.amount : undefined)
    ?? ((p.amount ?? 0) - (p.recurring_off ?? 0));

  /** Ask the server what this plan costs with the code typed. Creates nothing
   *  and redeems nothing — the redemption is only counted after payment. */
  const applyCoupon = async (plan: BillingPlan | null) => {
    const code = coupon.trim();
    // The method was picked for the last quote; it goes with it, or a refused
    // re-Apply would leave a full-price checkout pinned to one rail.
    setCouponError(''); setQuote(null); setPayMethod('');
    if (!code || !plan) return;
    setCouponBusy(true);
    const res = await apiService.billingCouponPreview(userId, plan.code, code, autoRenew);
    setCouponBusy(false);
    const d: any = res.data;
    if (!res.success || !d?.success) {
      // The server's coupon messages are already written for customers
      // ("Coupon 'X' has expired"), so they are shown as-is.
      setCouponError(res.error || d?.error || 'That code could not be applied.');
      return;
    }
    if (!d.discount) {
      // The server says WHY it did not apply — already used, expired, or
      // one-time-only. That wording is written for customers, so it beats a
      // generic line that leaves them with no next step.
      setCouponError(d.coupon_error
        || 'That code does not change the price of this plan.');
      return;
    }
    // A live campaign can beat — and mask — an invalid code: the price really
    // does drop, but not because of what they typed. Say both, or they leave
    // believing a dead code works.
    if (d.coupon_error) setCouponError(d.coupon_error);
    // One method: choose it silently. Several: the customer picks below.
    // None listed means the discount is not method-restricted at all.
    const ms: string[] = d.methods || [];
    setPayMethod(ms.length === 1 ? ms[0] : '');
    setQuote(d);
  };

  const startOneTime = async (plan: BillingPlan, code?: string) => {
    resetNotices(); setBusyPlan(plan.code);
    const res = await apiService.billingOrder(userId, plan.code, (code ?? coupon).trim() || undefined);
    if (!res.success || !res.data?.success) {
      setError(res.error || 'Could not create the order');
      setBusyPlan('');
      return;
    }
    openCheckout({
      keyId: res.data.key_id,
      description: `${plan.name} — one year, no auto-renewal`,
      orderId: res.data.razorpay_order_id,
      amountPaise: res.data.amount_paise,
      currency: res.data.currency,
      plan: plan.code, flow: 'order',
    });
  };

  // Razorpay hosts an invoice for subscription charges; one-time orders have
  // none, so we render a printable receipt from our own record instead of
  // showing a Download link that leads nowhere.
  const downloadInvoice = async (paymentId: string) => {
    const res = await apiService.billingReceipt(userId, paymentId);
    if (!res.success || !res.data?.success) {
      setError(res.error || 'Could not fetch that receipt');
      return;
    }
    const r = res.data;
    if (r.hosted_invoice_url) { window.open(r.hosted_invoice_url, '_blank', 'noopener'); return; }
    const win = window.open('', '_blank');
    if (!win) { setError('Allow pop-ups to download the receipt.'); return; }
    const esc = (v: any) => String(v ?? '').replace(/[<>&]/g, c =>
      ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c] as string));
    win.document.write(`<!doctype html><html><head><meta charset="utf-8">
      <title>Receipt ${esc(r.payment_id)}</title><style>
      body{font-family:system-ui,sans-serif;margin:48px;color:#1c1c1c}
      h1{font-size:20px;margin:0 0 4px} .muted{color:#777;font-size:13px}
      table{border-collapse:collapse;margin-top:24px;width:100%;max-width:520px}
      td{padding:8px 0;border-bottom:1px solid #eee;font-size:14px}
      td:last-child{text-align:right}
      .total{font-weight:700;border-bottom:none;padding-top:16px}
      </style></head><body>
      <h1>UniversityTech.io</h1>
      <div class="muted">Payment receipt · ${esc(r.payment_id)}</div>
      <table>
        <tr><td>Billed to</td><td>${esc(r.billed_to.name)} &lt;${esc(r.billed_to.email)}&gt;</td></tr>
        <tr><td>Date</td><td>${esc(new Date(r.date).toLocaleDateString())}</td></tr>
        <tr><td>Description</td><td>${esc(r.description)}</td></tr>
        ${r.method ? `<tr><td>Payment method</td><td>${esc(r.method)}</td></tr>` : ''}
        ${r.coupon ? `<tr><td>Coupon</td><td>${esc(r.coupon)}</td></tr>` : ''}
        <tr class="total"><td>Total paid</td><td>${r.amount_paise != null
          ? esc(money(r.amount_paise, r.currency)) : '—'}</td></tr>
      </table>
      <p class="muted" style="margin-top:32px">Questions? info@universitytech.io</p>
      <script>window.print()</script></body></html>`);
    win.document.close();
  };

  /** Confirm in the sheet — either exit. No confirm(): the modal IS the
   *  confirmation, and the reasons ride along for the admin table. A trial
   *  ends at the gateway now; a membership only stops renewing, and the
   *  server files a 'confirmed' event for both inside the cancel itself, so
   *  only the PostHog click goes from here. */
  const confirmCancel = async (reasons: string[], other: string) => {
    const kind: CancelKind = cancelSheet || 'trial';
    trackClick(CLICK_NAMES[kind].confirm, { reasons });
    // Clicking Confirm settles the sheet's outcome whatever the request
    // returns: someone whose trial lapsed while the sheet was up (404/409),
    // or who tabs away while the gateway call runs, did not "keep" anything,
    // and the server may be writing 'confirmed' for this same open.
    cancelSheetOpen.current = null;
    setCancelSheetBusy(true); setCancelSheetError('');
    const res = kind === 'trial'
      ? await apiService.billingCancelTrial(userId, reasons, other)
      : await apiService.billingCancel(userId, reasons, other);
    setCancelSheetBusy(false);
    if (res.success && res.data?.success) {
      setCancelSheet(null);
      setError('');
      // The cancel route answers with the date access runs to; the summary's
      // own current_end is the fallback for an older backend that does not.
      const until = fmtDate(res.data.access_until || summary?.subscription?.current_end);
      setNotice(kind === 'trial'
        ? `Your trial is cancelled — you won't be charged. Access continues until ${until}.`
        : `Your membership will not renew. Access continues until ${until}.`);
      // The come-back offer rides on the same answer ({code, message, …})
      // when the admin has it switched on for this kind of exit; an older
      // backend, or a disabled offer, simply carries none. The same code is
      // in the cancellation e-mail, so this is a convenience, not the only
      // copy they get.
      const w = res.data.winback;
      setWinbackCopied(false);
      setWinback(w && w.code
        ? { code: String(w.code), message: String(w.message || '') }
        : null);
      refresh();
    } else {
      setCancelSheetError(res.error || res.data?.error
        || 'Could not cancel — contact info@universitytech.io');
    }
  };

  /** Opening the sheet is the first thing the admin panel wants to know —
   *  "did people even click it" — so it is logged to both stores before the
   *  sheet paints. Fire-and-forget: neither call may hold up the dialog. */
  const openCancelSheet = (kind: CancelKind) => {
    setCancelSheetError('');
    setCancelSheet(kind);
    cancelSheetOpen.current = kind;
    trackClick(CLICK_NAMES[kind].open, { plan: summary?.subscription?.plan_code });
    void apiService.billingTrialEvent(userId, 'opened', [], '', kind);
  };

  /** Any way out that is not a successful cancel — Keep my trial/membership,
   *  ×, Escape, the backdrop, or leaving the page with the sheet up — counts
   *  once, against the kind that was opened. */
  const noteSheetKept = () => {
    const kind = cancelSheetOpen.current;
    if (!kind) return;
    cancelSheetOpen.current = null;
    trackClick(CLICK_NAMES[kind].keep);
    void apiService.billingTrialEvent(userId, 'kept', [], '', kind);
  };
  const dismissCancelSheet = () => { setCancelSheet(null); noteSheetKept(); };
  // Navigating off the tab with the sheet open is backing out too; without
  // this the funnel would show an open with no outcome.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => noteSheetKept(), []);

  if (loading) {
    return <div className="text-sm text-grayscale-1100 p-4">Loading billing…</div>;
  }

  const sub = summary?.subscription || null;
  const cur = summary?.currency || 'INR';
  // Discounts come from the server per plan AND per flow — a campaign can
  // apply to a one-time payment but not to a recurring plan (Razorpay needs
  // an offer for that). Recomputing them here advertised prices that were
  // never charged.
  // A discount with several method-scoped offers cannot be attached until we
  // know which one, so the subscribe buttons wait for that choice rather than
  // silently dropping the discount.
  const needsMethod = !!quote && (quote.methods || []).length > 1 && !payMethod;
  const payNow = (p: BillingPlan) => (p.amount ?? 0) - (p.one_time_off ?? 0);
  const renewNow = (p: BillingPlan) => (p.amount ?? 0) - (p.recurring_off ?? 0);
  const autoRenew = summary?.auto_renew_available !== false;
  // A trial is NOT a paid subscription: treating it as one hid every buy
  // button during exactly the week we are trying to convert them.
  const onTrial = !!sub && (sub.is_trial || sub.source === 'trial');
  const hasPaidActive = !!sub && sub.status === 'active' && !onTrial
    && (currentPlan?.price_inr_paise ?? 0) > 0;
  // Where the account is in the trial lifecycle. The server's word when it
  // gives one; otherwise derived the old way, so a frontend deployed ahead
  // of the backend still shows the right thing. Note the summary drops a
  // subscription row once its current_end has passed, so a lapsed trial can
  // only be told from `trial.used_reason` here, never from `sub`.
  const state: BillingState = summary?.state
    || (hasPaidActive ? 'voyager'
      : onTrial && sub!.status === 'active' ? 'trial_active'
      : summary?.trial?.used_reason === 'lapsed' ? 'trial_lapsed'
      : 'explorer');
  const trialEndsAt = summary?.trial_lifecycle?.ends_at || (onTrial ? sub!.current_end : null);
  const trialDaysLeft = summary?.trial_lifecycle?.days_left ?? daysUntil(trialEndsAt);
  const trialPlanName = summary?.plans.find(p => p.code === summary.trial?.plan_code)?.name
    || currentPlan?.name || 'Voyager';
  // A cancelled trial still runs to its end date; that is its own line in
  // the plan card ("Trial cancelled — access until…"), so the lapsed notice
  // waits for the access to actually be gone.
  const trialLapsedNow = state === 'trial_lapsed' && !(onTrial && sub!.status === 'cancelled');
  // The tab's own trial button: hidden only when there is no trial to speak
  // of (switched off, or already paying); greyed, with the reason, once it
  // has been used — one trial per account, ever.
  const isMember = state === 'voyager' || state === 'voyager_assigned';
  const trialShown = !!summary?.trial && summary.trial.enabled !== false && !isMember;
  const trialOffered = trialShown && state !== 'trial_active'
    && !summary!.trial.used && summary!.trial.available;
  const trialBlockedReason = trialShown && !trialOffered && state !== 'trial_active'
    ? (summary!.trial.reason || 'You have already used your free trial.') : '';

  return (
    <div className="flex flex-col gap-4 max-w-3xl">
      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
      {summary?.promotion && (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-primary-100 border border-primary-300 px-4 py-3">
          <div className="text-sm text-grayscale-1300">
            <span className="font-bold">{summary.promotion.name}</span>
            {summary.promotion.description ? ` — ${summary.promotion.description}` : ''}
            {summary.promotion.intro_cycles > 0 && (
              <span className="text-grayscale-1200"> (first {summary.promotion.intro_cycles} payments)</span>
            )}
            {summary.plans.every(p => (p.recurring_off ?? 0) === 0)
              && summary.plans.some(p => (p.one_time_off ?? 0) > 0) && (
              <span className="text-grayscale-1200"> · applies to one-time payments</span>
            )}
          </div>
          {summary.promotion.ends_at && (
            <span className="text-xs text-primary-700 shrink-0">
              Ends {fmtDate(summary.promotion.ends_at)}
            </span>
          )}
        </div>
      )}
      {notice && (
        <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          {notice}
          {/* The come-back coupon, under the cancel confirmation. Message
              and code are both the server's: the admin picks the promotion
              and its wording on the Trials tab, so nothing here hardcodes a
              percentage that the price check may no longer honour. */}
          {winback && (
            <div className="mt-2 pt-2 border-t border-green-200 flex items-center gap-2 flex-wrap">
              {winback.message && <span className="text-grayscale-1300">{winback.message}</span>}
              <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-300 bg-white pl-3 pr-1 py-0.5">
                <span className="font-mono text-xs font-bold tracking-wider text-primary-700 select-all">
                  {winback.code}
                </span>
                <button type="button" onClick={copyWinback}
                  className="text-[11px] font-medium rounded-full px-2 py-0.5 bg-primary-500 text-grayscale-100 hover:bg-primary-700">
                  {winbackCopied ? `${winback.code} copied` : 'Copy'}
                </button>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Current plan */}
      <div className="rounded-2xl border border-primary-300 bg-primary-100/40 p-5">
        <div className="text-[11px] font-bold tracking-widest text-primary-700 uppercase mb-1">Current plan</div>
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-2xl font-bold text-grayscale-1300 font-grotesk">{currentPlan?.name || 'Explorer'}</span>
          {currentPlan && (currentPlan.amount ?? 0) > 0 && (
            <span className="text-sm text-grayscale-1100">
              {money(currentPlan.amount, cur)} / user / yr
            </span>
          )}
        </div>
        <div className="text-sm text-grayscale-1200 mt-1">{currentPlan?.description}</div>
        {/* Only while the trial is live: the summary keeps returning a
            cancelled trial row until its access ends, and "ends {date}" above
            "cancelled" read as two contradicting statuses with one date. */}
        {onTrial && sub.status === 'active' && (
          <div className="flex items-center justify-between gap-3 flex-wrap mt-2">
            <div className="text-xs text-primary-700">
              {/* The plan's name and a countdown, not "Free trial": the
                  thing on trial is Voyager, and "(2 days left)" is the
                  number that makes someone decide. */}
              {trialPlanName} trial · ends {fmtDate(sub.current_end)} ({trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} left).
              {' '}Upgrade any time to keep access.
              {/* The way out sits with the trial itself, not in the footer's
                  contact line: a mandate trial WILL charge when it ends, and a
                  customer who cannot find the cancel is a refund later. */}
              <button type="button" onClick={() => openCancelSheet('trial')}
                className="ml-2 underline hover:no-underline">
                Cancel trial
              </button>
            </div>
            {upgradeTarget && (
              <button type="button" onClick={() => setShowPricing(true)} disabled={!!busyPlan}
                className="py-2 px-3.5 rounded-full font-grotesk text-xs font-medium bg-primary-500 text-grayscale-100 disabled:opacity-50 shrink-0">
                Upgrade to {upgradeTarget.name}
              </button>
            )}
          </div>
        )}
        {sub?.status === 'cancelled' && (
          <div className="text-xs text-amber-700 mt-2">
            {onTrial
              ? <>Trial cancelled — you won't be charged. Access until {fmtDate(sub.current_end)}.</>
              : <>Auto-renewal cancelled — access until {fmtDate(sub.current_end)}.</>}
          </div>
        )}
      </div>

      {/* Trial over, never paid (Sep 2026 spec: a stored state of its own,
          not "explorer again"). The access is Explorer's; the notice is what
          tells them why the trial button below is greyed, and the Upgrade
          button is the one door that is still open. */}
      {trialLapsedNow && (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
          <div className="text-sm text-amber-800">
            <span className="font-bold">Your free trial has ended.</span>
            {' '}Upgrade to {upgradeTarget?.name || trialPlanName} to keep full access.
            {summary?.trial_lifecycle?.ends_at && (
              <span className="text-amber-700"> Ended {fmtDate(summary.trial_lifecycle.ends_at)}.</span>
            )}
          </div>
          {upgradeTarget && (
            <button type="button" onClick={() => setShowPricing(true)} disabled={!!busyPlan}
              className="py-2 px-3.5 rounded-full font-grotesk text-xs font-medium bg-primary-500 text-grayscale-100 disabled:opacity-50 shrink-0">
              Upgrade to {upgradeTarget.name}
            </button>
          )}
        </div>
      )}

      {/* Upgrade banner */}
      {/* Only when the modal can actually show the plan being promised. It
          renders the free tier and the one paid plan on sale; a Voyager member
          used to be shown a banner for Navigator that opened a modal with no
          Navigator in it and nothing to press. */}
      {upgradeTarget && MODAL_PLANS.includes(upgradeTarget.code) && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-grayscale-300 bg-white p-4">
          <div className="text-sm text-grayscale-1200">
            Unlock {upgradeTarget.description.replace(/^Everything in \w+, plus /i, '')}
            {' with '}<span className="font-bold text-grayscale-1300">{upgradeTarget.name}</span>.
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setShowPricing(true)}
              className="py-2.5 px-4 rounded-full font-grotesk text-sm font-medium border border-primary-500 text-primary-700"
            >View pricing</button>
          </div>
        </div>
      )}

      {/* Subscribe options when on the free tier */}
      {(!hasPaidActive || onTrial) && summary && (
        <div className="rounded-xl border border-grayscale-300 bg-white p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm font-bold text-grayscale-1300 font-grotesk">
              {onTrial ? 'Keep your access — choose a plan' : 'Choose a plan'}
            </div>
            {trialShown && (
              <div className="flex flex-col items-end gap-1">
                <button
                  onClick={() => startTrial()}
                  disabled={!trialOffered || !!busyPlan}
                  title={trialBlockedReason || undefined}
                  className={`py-2 px-3.5 rounded-full font-grotesk text-xs font-medium border ${
                    trialOffered
                      ? 'border-primary-500 text-primary-700 disabled:opacity-50'
                      : 'border-grayscale-300 bg-grayscale-200 text-grayscale-1100 cursor-not-allowed'}`}
                >{state === 'trial_active'
                    ? `Trial active · ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left`
                    : <>Start {summary.trial.days}-day free trial
                      {trialOffered && (
                        <span className="font-normal opacity-70"> · no card required</span>
                      )}</>}
                </button>
                {/* One trial per account, ever: the greyed button says why. */}
                {trialBlockedReason && (
                  <span className="text-[11px] text-grayscale-1100">{trialBlockedReason}</span>
                )}
              </div>
            )}
          </div>
          {summary.plans.filter(p => (p.amount ?? p.price_inr_paise) > 0 && p.is_active !== false).map(p => (
            <div key={p.code} className="flex items-center justify-between gap-3 border-t border-grayscale-200 pt-3">
              <div>
                <div className="text-sm font-medium text-grayscale-1300">{p.name}
                  <span className="text-grayscale-1100 font-normal"> · {
                    // Show the discount for the flow this customer will
                    // actually use. A promo without a Razorpay offer id
                    // discounts pay-once only, so on auto-renew the price
                    // must read full — otherwise we advertise 8,100 and
                    // Razorpay charges 9,000.
                    (autoRenew ? (p.recurring_off ?? 0) : (p.one_time_off ?? 0)) > 0 ? (
                      <>
                        <s className="text-grayscale-1100">{money(p.amount, cur)}</s>{' '}
                        <span className="text-primary-700 font-medium">
                          {money(autoRenew ? renewNow(p) : payNow(p), cur)}
                        </span>
                      </>
                    ) : money(p.amount, cur)
                  } per user / yr</span>
                </div>
                <div className="text-xs text-grayscale-1100">{p.description}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                {autoRenew && (() => {
                  // A code in USD / EUR / CHF is redeemed on a one-year payment
                  // (quote.pay_once): the button pays that instead of auto-renew.
                  const once = !!quote?.pay_once && !couponError && upgradeTarget?.code === p.code;
                  return (
                    <button onClick={() => (once
                      ? startOneTime(p, codeToSend())
                      : startSubscribe(p, codeToSend(), payMethod || undefined, false, shownPrice(p)))}
                      disabled={!!busyPlan || (!once && needsMethod)}
                      className="py-2 px-3.5 rounded-full font-grotesk text-xs font-medium bg-primary-500 text-grayscale-100 disabled:bg-primary-100 disabled:text-primary-300">
                      {busyPlan === p.code ? 'Opening…'
                        : once ? `Pay ${money(quote.amount, quote.currency)} for one year`
                        : (p.recurring_off ?? 0) > 0
                          ? `Become a member — ${money(renewNow(p), cur)}`
                          : 'Become a member'}
                    </button>
                  );
                })()}
                {summary.one_time_allowed && (
                  <button onClick={() => startOneTime(p)} disabled={!!busyPlan}
                    className={`py-2 px-3.5 rounded-full font-grotesk text-xs font-medium disabled:opacity-50 ${
                      autoRenew ? 'border border-primary-500 text-primary-700'
                                : 'bg-primary-500 text-grayscale-100'}`}>
                    {busyPlan === p.code ? 'Opening…'
                      : autoRenew ? 'Pay once' : `Pay ${money(payNow(p), cur)}`}
                  </button>
                )}
              </div>
            </div>
          ))}
          {!autoRenew && (
            <div className="text-xs text-grayscale-1100 pt-1">
              {/* Said "Billed in USD" whatever the customer was actually charged
                  in, contradicting the price printed directly above it. */}
              Billed in {cur}. Auto-renewal is not available for international
              cards yet — this is a single payment covering one year.
            </div>
          )}
          {/* Shown on both flows. A one-time order takes any coupon; a
              subscription takes a coded promotion carrying a Razorpay offer.
              Gating this on one_time_allowed hid it from every subscriber,
              which is why the field appeared not to exist. */}
          <div className="flex flex-col gap-1.5 pt-1">
              <div className="flex items-center gap-2 flex-wrap">
                <input value={coupon}
                  onChange={e => { setCoupon(e.target.value); setQuote(null); setCouponError(''); setPayMethod(''); }}
                  onKeyDown={e => { if (e.key === 'Enter') applyCoupon(upgradeTarget); }}
                  placeholder="Coupon or voucher code"
                  className="h-9 px-2.5 rounded-md border-primary-300 border-[0.5px] bg-white text-sm w-64" />
                <button type="button" onClick={() => applyCoupon(upgradeTarget)}
                  disabled={couponBusy || !coupon.trim() || !upgradeTarget}
                  className="h-9 px-4 rounded-full border border-primary-500 text-primary-700 text-sm font-medium disabled:opacity-50">
                  {couponBusy ? 'Checking…' : 'Apply'}
                </button>
              </div>
              {couponError && <span className="text-xs text-red-600">{couponError}</span>}
              {quote && (
                <span className="text-xs text-primary-700">
                  {quote.coupon
                    ? `${quote.coupon} applied`
                    : `${quote.promotion || 'Discount'} applied`} —{' '}
                  <span className="line-through text-grayscale-1100">
                    {money(quote.base_amount, quote.currency)}
                  </span>{' '}
                  <b>{money(quote.amount, quote.currency)}</b>
                  {quote.pay_once
                    ? ' for one year, paid once (no auto-renewal).'
                    : quote.renews_at && quote.renews_at !== quote.amount
                    ? ` for the first year, then ${money(quote.renews_at, quote.currency)}/year.`
                    : ' for one year.'}
                </span>
              )}
              {/* Asked only when the discount has offers for more than one
                  method. One method is preselected silently; an unrestricted
                  discount never shows this at all. */}
              {quote && (quote.methods || []).length > 1 && (
                <div className="flex items-center gap-2 flex-wrap pt-0.5">
                  <span className="text-xs text-grayscale-1100">Pay with</span>
                  {(quote.methods || []).map((m: string) => (
                    <button key={m} type="button" onClick={() => setPayMethod(m)}
                      className={`py-1 px-3 rounded-full text-xs font-medium border ${
                        payMethod === m
                          ? 'bg-primary-500 text-grayscale-100 border-primary-500'
                          : 'border-primary-300 text-primary-700'}`}>
                      {m === 'upi' ? 'UPI' : m === 'card' ? 'Card' : m}
                    </button>
                  ))}
                  {!payMethod && (
                    <span className="text-[11px] text-grayscale-1100">
                      — this discount is tied to how you pay, so pick one to continue
                    </span>
                  )}
                </div>
              )}
          </div>
        </div>
      )}

      {/* Renewal */}
      {sub && (
        <div className="flex items-center justify-between rounded-xl border border-grayscale-300 bg-white p-4">
          <div>
            <div className="text-sm text-grayscale-1200">{sub.auto_renew ? 'Renewal date' : 'Access until'}</div>
            <div className="text-xs text-primary-700">
              {/* A trial never had a payment, so the paid-flow copy below
                  ("one-time payment") is wrong for it in every state. */}
              {sub.source === 'manual' ? 'Granted by the UniversityTech team'
                : sub.source === 'manual_payment' && !sub.auto_renew ? 'Paid — no auto-renewal'
                : onTrial
                  ? (sub.status === 'cancelled' ? 'Trial cancelled — nothing will be charged'
                    : sub.auto_renew ? 'First payment on this date unless you cancel'
                    : 'Free trial — no card on file')
                : sub.auto_renew
                  ? 'Renews automatically — cancel any time before this date'
                  : 'One-time payment — no auto-renewal'}
            </div>
          </div>
          <div className="text-sm font-bold text-grayscale-1300">{fmtDate(sub.current_end)}</div>
        </div>
      )}

      {/* Turn on auto-renewal: a paid period with no renewal adds a card for
          the next one; nothing is charged until access would end. */}
      {summary?.renewal && (
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-primary-300 bg-primary-100 p-4">
          <div className="min-w-0">
            <div className="text-sm font-bold text-grayscale-1300">Keep your {summary.renewal.plan_name} membership going</div>
            <div className="text-xs text-grayscale-1200 mt-0.5">
              Add a card now and it renews automatically on {fmtDate(summary.renewal.charge_on)} for{' '}
              {money(summary.renewal.amount, summary.renewal.currency)}/year. Nothing is charged before then, and you can cancel any time.
            </div>
          </div>
          <button type="button" onClick={startRenewal} disabled={busyPlan === 'renewal'}
            className="h-9 px-4 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-60">
            {busyPlan === 'renewal' ? 'Opening…' : 'Turn on auto-renewal'}
          </button>
        </div>
      )}

      {/* Payment method */}
      {summary?.payment_method && (
        <div className="flex items-center justify-between rounded-xl border border-grayscale-300 bg-white p-4">
          <div>
            <div className="text-sm text-grayscale-1200">Payment method</div>
            <div className="text-sm font-bold text-grayscale-1300">{summary.payment_method}</div>
          </div>
          {currentPlan && hasPaidActive ? (
            <button
              onClick={() => startSubscribe(currentPlan)}
              disabled={!!busyPlan}
              className="py-2 px-4 rounded-full font-grotesk text-sm font-medium border border-grayscale-400 text-grayscale-1300 disabled:opacity-40"
            >Update</button>
          ) : (
            <span className="text-xs text-grayscale-1100">Charged at your next payment</span>
          )}
        </div>
      )}

      {/* Invoice history */}
      {summary && summary.payments.length > 0 && (
        <div className="rounded-xl border border-grayscale-300 bg-white p-4">
          <div className="text-sm font-bold text-grayscale-1300 font-grotesk mb-2">Invoice history</div>
          {summary.payments.map(p => (
            <div key={p.payment_id} className="flex items-center justify-between gap-3 border-t border-grayscale-200 py-2 text-sm">
              <span className="text-grayscale-1200">{fmtDate(p.date)} · {p.description}
                {p.coupon ? <span className="text-xs text-grayscale-1100"> (coupon {p.coupon})</span> : null}
              </span>
              <span className="flex items-center gap-4 shrink-0">
                <span className="font-medium text-grayscale-1300">{money(p.amount_paise, p.currency)}</span>
                <button onClick={() => downloadInvoice(p.payment_id)}
                        className="text-primary-700 underline hover:no-underline">Download</button>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Voucher + cancel + contact */}
      {summary?.voucher && (
        <div className="text-xs text-grayscale-1100">
          Voucher applied at signup: <span className="font-bold text-grayscale-1300">{summary.voucher}</span>
        </div>
      )}
      {/* One line, three states. A renewal that can still be stopped gets the
          button and nothing else — pairing it with "contact us" read as two
          competing instructions. A paid period with no renewal has nothing to
          cancel, so say what will happen instead of pointing at an inbox. The
          contact line survives only where there is genuinely nothing to do
          in-app (free tier, a lapsed row, a row already cancelled). A
          team-granted pack is an active paid row with no renewal, so it
          gets the middle line like any other. */}
      <div className="text-xs text-grayscale-1100 flex items-center gap-3 flex-wrap">
        {hasPaidActive && sub?.auto_renew ? (
          <button onClick={() => openCancelSheet('subscription')}
            className="underline hover:text-grayscale-1300">
            Cancel membership
          </button>
        ) : sub?.status === 'active' && !onTrial && !sub.auto_renew ? (
          <span>No renewal scheduled — access ends {fmtDate(sub.current_end)}.</span>
        ) : (
          <span>To cancel your membership, contact us at info@universitytech.io</span>
        )}
      </div>

      {showPricing && summary && (
        <PricingModal
          plans={summary.plans}
          featureOrder={summary.feature_order || []}
          // A trial of Voyager is not Voyager being current: they have not
          // bought it, and marking it current hid the only button that lets
          // them.
          currentPlanCode={onTrial ? 'explorer' : currentPlan?.code}
          busyPlan={busyPlan}
          onChoose={(p, code, method, expected, payOnce) => {
            setShowPricing(false);
            // The modal has its own coupon box, and hands back what was
            // applied THERE. This used to ignore both arguments and read the
            // tab's own state instead, so a discount the modal had just shown
            // as applied was not what the checkout charged.
            const applied = code ?? (quote && !couponError ? coupon.trim() : undefined);
            // The flow is the account's, not the code's: a currency without
            // autopay takes the one-time order whether or not a code survived.
            // A code in USD / EUR / CHF is redeemed on a one-year payment
            // (payOnce), whatever the account's usual flow.
            if (payOnce || (!autoRenew && summary?.one_time_allowed)) startOneTime(p, applied ?? '');
            else startSubscribe(p, applied, method ?? (payMethod || undefined), false, expected);
          }}
          trial={summary.trial}
          state={state}
          trialDaysLeft={trialDaysLeft}
          onStartTrial={p => { setShowPricing(false); startTrial(p); }}
          onClose={() => setShowPricing(false)}
        />
      )}
      {trialForm && summary && (
        <TrialFormModal
          open
          initial={trialForm.initial}
          days={summary.trial?.days || 3}
          onSubmit={submitTrialForm}
          onClose={() => setTrialForm(null)}
        />
      )}
      {cancelSheet && (
        <CancelTrialModal
          kind={cancelSheet}
          // The sheet's reassurance line: what they paid for is theirs until
          // this date whatever they tick. Only the membership exit shows it.
          accessUntil={cancelSheet === 'subscription' ? fmtDate(sub?.current_end) : undefined}
          reasons={summary?.trial_cancel_reasons?.length
            ? summary.trial_cancel_reasons : TRIAL_CANCEL_REASONS}
          busy={cancelSheetBusy}
          error={cancelSheetError}
          onConfirm={confirmCancel}
          onClose={dismissCancelSheet}
        />
      )}
    </div>
  );
};

export default BillingTab;
